import { useEffect, useState } from "react";
import { IntegrationCard } from "../components/integrations/IntegrationCard";
import { GarminConnectModal } from "../components/integrations/GarminConnectModal";
import { useIntegrationsStore } from "../stores/integrationsStore";
import { useAuthStore } from "../stores/authStore";

// Whitelist of emails allowed to access Garmin integration
const GARMIN_ALLOWED_EMAILS = [
  "ppikula@gmail.com",
  "ppikula+demo@gmail.com",
  "studzien@gmail.com",
  "adam.niepokoj@gmail.com"
];

// Garmin icon component
function GarminIcon() {
  return (
    <svg
      className="w-8 h-8 text-blue-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
  );
}

// Strava icon component (official Strava brand colors)
function StravaIcon() {
  return (
    <svg
      className="w-8 h-8"
      viewBox="0 0 24 24"
      fill="#FC4C02"
    >
      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
    </svg>
  );
}

export function SettingsIntegrationsPage() {
  const {
    integrations,
    connectModalOpen,
    connectModalProvider,
    fetchStatus,
    connect,
    disconnect,
    fetchActivities,
    openConnectModal,
    closeConnectModal,
    initiateOAuth,
  } = useIntegrationsStore();

  const [oauthMessage, setOauthMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const { user } = useAuthStore();
  const canAccessGarmin = user?.email && GARMIN_ALLOWED_EMAILS.includes(user.email);

  const garminIntegration = integrations["garmin"];
  const stravaIntegration = integrations["strava"];

  // Fetch status on mount and handle OAuth callback params
  useEffect(() => {
    if (canAccessGarmin) {
      fetchStatus("garmin");
    }
    fetchStatus("strava");

    // Handle OAuth callback query params
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const error = params.get("error");
    const message = params.get("message");

    if (connected === "strava") {
      setOauthMessage({ type: "success", text: "Successfully connected to Strava!" });
      fetchStatus("strava");
      // Clear URL params
      window.history.replaceState({}, "", window.location.pathname);
    } else if (error) {
      let errorText = "Failed to connect to Strava";
      if (error === "strava_denied") {
        errorText = "Strava authorization was denied";
      } else if (error === "invalid_state") {
        errorText = "Invalid or expired authorization. Please try again.";
      } else if (message) {
        errorText = decodeURIComponent(message);
      }
      setOauthMessage({ type: "error", text: errorText });
      // Clear URL params
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchStatus, canAccessGarmin]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (oauthMessage?.type === "success") {
      const timer = setTimeout(() => setOauthMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [oauthMessage]);

  const handleGarminConnect = async (email: string, password: string) => {
    return await connect("garmin", email, password);
  };

  const handleGarminDisconnect = async () => {
    await disconnect("garmin");
  };

  const handleGarminSync = async () => {
    await fetchActivities("garmin", { limit: 20 });
  };

  const handleStravaConnect = () => {
    initiateOAuth("strava");
  };

  const handleStravaDisconnect = async () => {
    await disconnect("strava");
  };

  const handleStravaSync = async () => {
    await fetchActivities("strava", { limit: 20 });
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Integrations</h1>

      <div className="max-w-2xl space-y-4">
        <p className="text-gray-600 mb-6">
          Connect your fitness accounts to sync your activities and get personalized insights.
        </p>

        {/* OAuth callback message */}
        {oauthMessage && (
          <div
            className={`p-4 rounded-lg mb-4 ${
              oauthMessage.type === "success"
                ? "bg-green-50 border border-green-200 text-green-800"
                : "bg-red-50 border border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2">
              {oauthMessage.type === "success" ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span>{oauthMessage.text}</span>
              <button
                onClick={() => setOauthMessage(null)}
                className="ml-auto text-gray-500 hover:text-gray-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {canAccessGarmin && (
          <IntegrationCard
            name="Garmin Connect"
            description="Sync your running activities and health data from Garmin"
            icon={<GarminIcon />}
            status={garminIntegration?.status ?? "disconnected"}
            lastSyncAt={garminIntegration?.lastSyncAt ?? null}
            errorMessage={garminIntegration?.errorMessage ?? null}
            isLoading={garminIntegration?.isLoading ?? false}
            needsReconnect={garminIntegration?.needsReconnect ?? false}
            onConnect={() => openConnectModal("garmin")}
            onDisconnect={handleGarminDisconnect}
            onSync={garminIntegration?.status === "connected" ? handleGarminSync : undefined}
          />
        )}

        <IntegrationCard
          name="Strava"
          description="Sync your running activities from Strava"
          icon={<StravaIcon />}
          status={stravaIntegration?.status ?? "disconnected"}
          lastSyncAt={stravaIntegration?.lastSyncAt ?? null}
          errorMessage={stravaIntegration?.errorMessage ?? null}
          isLoading={stravaIntegration?.isLoading ?? false}
          needsReconnect={stravaIntegration?.needsReconnect ?? false}
          onConnect={handleStravaConnect}
          onDisconnect={handleStravaDisconnect}
          onSync={stravaIntegration?.status === "connected" ? handleStravaSync : undefined}
        />
      </div>

      {/* Garmin Connect Modal */}
      {canAccessGarmin && (
        <GarminConnectModal
          isOpen={connectModalOpen && connectModalProvider === "garmin"}
          isLoading={garminIntegration?.isLoading ?? false}
          error={garminIntegration?.errorMessage ?? null}
          onClose={closeConnectModal}
          onConnect={handleGarminConnect}
        />
      )}
    </div>
  );
}
